/**
 * Background Upload Service
 * Uses native iOS/Android background upload capabilities via @capgo/capacitor-uploader
 * Falls back to standard upload for PWA
 *
 * Flow:
 * 1. Save image to filesystem (required for native background upload)
 * 2. Get presigned URL from server
 * 3. Start native background upload (continues even when app closed)
 * 4. On completion, call server to finalize (create database record)
 */

import { Uploader, UploadEvent } from '@capgo/capacitor-uploader';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { isNativeApp } from '../utils/platform.utils';
import api from '../api/auth.api';
import { API_CONFIG } from '../config/api.config';

export interface BackgroundUploadOptions {
  type: 'truck_ticket' | 'daily_report' | 'photo' | 'asphalt_photo' | 'tm_ticket' | 'equipment_issue' | 'asphalt_report' | 'equipment_repair' | 'equipment_repair_update' | 'receipt';
  jobId?: number;
  captureTime: Date;
  metadata?: Record<string, any>;
}

export interface UploadProgress {
  id: string;
  percent: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  error?: string;
}

class BackgroundUploadService {
  private uploadCallbacks: Map<string, (event: UploadProgress) => void> = new Map();
  private initialized = false;

  /**
   * Initialize the uploader and set up event listeners
   */
  async initialize() {
    if (this.initialized || !isNativeApp()) return;

    try {
      this.debug('Initializing Uploader event listener...');
      // Listen for upload events
      await Uploader.addListener('events', (event: UploadEvent) => {
        this.debug(`Event received: ${event.name}, id=${event.id}, payload=${JSON.stringify(event.payload)}`);

        const callback = this.uploadCallbacks.get(event.id);

        switch (event.name) {
          case 'uploading':
            if (callback) {
              callback({
                id: event.id,
                percent: event.payload?.percent || 0,
                status: 'uploading'
              });
            }
            break;

          case 'completed':
            console.log(`[BackgroundUpload] Upload ${event.id} completed`);
            if (callback) {
              callback({
                id: event.id,
                percent: 100,
                status: 'completed'
              });
            }
            // Finalize will be called separately after this event
            break;

          case 'failed':
            console.error(`[BackgroundUpload] Upload ${event.id} failed:`, event.payload);
            if (callback) {
              callback({
                id: event.id,
                percent: 0,
                status: 'failed',
                error: event.payload?.error || 'Upload failed'
              });
            }
            this.uploadCallbacks.delete(event.id);
            break;
        }
      });

      this.initialized = true;
      this.debug('Service initialized successfully');
    } catch (error: any) {
      this.debug(`Failed to initialize: ${error?.message || error}`);
      console.error('[BackgroundUpload] Failed to initialize:', error);
    }
  }

  /**
   * Upload a file using native background upload
   * @param file - File or Blob to upload (can be null if base64Data provided)
   * @param filename - Original filename
   * @param options - Upload options (type, jobId, metadata)
   * @param onProgress - Progress callback
   * @param base64Data - Optional base64 data to skip File->base64 conversion (fixes iPad issues)
   */
  // Debug helper - adds to global debug log visible in UI
  private debug(msg: string) {
    console.log(`[BackgroundUpload] ${msg}`);
    (window as any).__uploadDebug = (window as any).__uploadDebug || [];
    (window as any).__uploadDebug.push(`[BG] ${msg}`);
    if ((window as any).__uploadDebug.length > 50) (window as any).__uploadDebug.shift();
  }

  async uploadFile(
    file: File | Blob | null,
    filename: string,
    options: BackgroundUploadOptions,
    onProgress?: (progress: UploadProgress) => void,
    base64Data?: string
  ): Promise<{ success: boolean; id?: number; error?: string }> {
    this.debug(`uploadFile called: ${filename}, type=${options.type}`);

    // For PWA, fall back to standard upload
    if (!isNativeApp()) {
      this.debug('Not native app, using PWA fallback');
      return this.uploadFilePWA(file, filename, options);
    }

    this.debug('Native app detected, initializing...');
    await this.initialize();
    this.debug('Initialized');

    try {
      // Step 1: Save file to device filesystem
      // Use provided base64 directly if available (avoids iPad double-conversion issues)
      let dataToSave: string;
      if (base64Data) {
        dataToSave = base64Data;
        this.debug(`Using provided base64: ${base64Data.length} chars`);
      } else if (file) {
        this.debug('Converting file to base64...');
        dataToSave = await this.fileToBase64(file);
        this.debug(`Converted to base64: ${dataToSave.length} chars`);
      } else {
        throw new Error('Either file or base64Data must be provided');
      }
      const tempFilename = `upload_${Date.now()}_${filename}`;

      this.debug(`Writing to filesystem: ${tempFilename}`);
      await Filesystem.writeFile({
        path: tempFilename,
        data: dataToSave,
        directory: Directory.Cache
      });
      this.debug('File written to filesystem');

      const fileUri = await Filesystem.getUri({
        path: tempFilename,
        directory: Directory.Cache
      });

      this.debug(`File URI: ${fileUri.uri}`);

      // Step 2: Get presigned URL from server
      this.debug('Requesting presigned URL...');
      const contentType = file?.type || 'image/jpeg';
      this.debug(`Content-type: ${contentType}, jobId: ${options.jobId}`);

      const presignResponse = await api.post(`${API_CONFIG.BASE_URL}/api/uploads/presign`, {
        type: options.type,
        filename: filename,
        job_id: options.jobId,
        content_type: contentType
      });

      this.debug(`Presign response: success=${presignResponse.data.success}`);

      if (!presignResponse.data.success) {
        this.debug(`Presign FAILED: ${presignResponse.data.error}`);
        throw new Error(presignResponse.data.error || 'Failed to get presigned URL');
      }

      const { upload_url, blob_name, container } = presignResponse.data.data;
      this.debug(`Got presigned URL: ${blob_name}`);

      // Step 3: Start native background upload - plugin generates its own ID
      this.debug('Starting native upload via Uploader plugin...');
      this.debug(`FilePath: ${fileUri.uri}`);
      this.debug(`ServerUrl: ${upload_url.substring(0, 100)}...`);

      const result = await Uploader.startUpload({
        filePath: fileUri.uri,
        serverUrl: upload_url,
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'x-ms-blob-type': 'BlockBlob'
        }
      });

      this.debug(`Uploader.startUpload returned: ${JSON.stringify(result)}`);

      const uploadId = result.id;

      if (onProgress) {
        this.uploadCallbacks.set(uploadId, onProgress);
      }
      this.debug(`Upload started with ID: ${uploadId}`);

      // Store finalization data for when upload completes
      const finalizationData = {
        type: options.type,
        blob_name,
        container,
        job_id: options.jobId,
        metadata: options.metadata || {},
        capture_time: options.captureTime.toISOString()
      };

      // Save finalization data to localStorage (survives app restart)
      localStorage.setItem(`pending_finalize_${uploadId}`, JSON.stringify(finalizationData));

      // Wait for upload to complete (or return immediately for true background)
      return await this.waitForUploadAndFinalize(uploadId, finalizationData, tempFilename);

    } catch (error: any) {
      this.debug(`ERROR: ${error?.message || error}`);
      console.error('[BackgroundUpload] Error:', error);

      // NATIVE OFFLINE QUEUE: If presigned URL failed, save file path for retry later
      // This avoids falling back to base64 IndexedDB queue
      if (isNativeApp() && error.message?.includes('Network') || error.message?.includes('Failed to fetch') || error.code === 'ERR_NETWORK') {
        try {
          const tempFilename = `upload_${Date.now()}_${filename}`;
          const base64Data = await this.fileToBase64(file);

          // Save file to filesystem if not already saved
          await Filesystem.writeFile({
            path: tempFilename,
            data: base64Data,
            directory: Directory.Cache
          });

          const fileUri = await Filesystem.getUri({
            path: tempFilename,
            directory: Directory.Cache
          });

          // Save to pending native uploads queue (file path, not base64 data)
          const pendingKey = `pending_native_upload_${Date.now()}`;
          const pendingData = {
            filePath: fileUri.uri,
            tempFilename,
            filename,
            options,
            contentType: file.type || 'image/jpeg',
            createdAt: new Date().toISOString(),
            retryCount: 0
          };
          localStorage.setItem(pendingKey, JSON.stringify(pendingData));
          console.log(`[BackgroundUpload] Saved to native offline queue: ${filename}`);

          return { success: true, id: -4 }; // -4 indicates native offline queue
        } catch (queueError) {
          console.error('[BackgroundUpload] Failed to save to native offline queue:', queueError);
        }
      }

      return { success: false, error: error.message };
    }
  }

  /**
   * Retry pending native uploads that failed due to network issues
   * Called on app resume or when network comes back online
   */
  async retryPendingNativeUploads() {
    if (!isNativeApp()) return;

    const keys = Object.keys(localStorage).filter(k => k.startsWith('pending_native_upload_'));
    if (keys.length === 0) return;

    console.log(`[BackgroundUpload] Retrying ${keys.length} pending native uploads...`);

    for (const key of keys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (!data.filePath || !data.options) continue;

        console.log(`[BackgroundUpload] Retrying: ${data.filename}`);

        // Try to get presigned URL
        const presignResponse = await api.post(`${API_CONFIG.BASE_URL}/api/uploads/presign`, {
          type: data.options.type,
          filename: data.filename,
          job_id: data.options.jobId,
          content_type: data.contentType
        });

        if (!presignResponse.data.success) {
          throw new Error(presignResponse.data.error || 'Failed to get presigned URL');
        }

        const { upload_url, blob_name, container } = presignResponse.data.data;
        console.log(`[BackgroundUpload] Got presigned URL for retry: ${blob_name}`);

        // Start native background upload
        const result = await Uploader.startUpload({
          filePath: data.filePath,
          serverUrl: upload_url,
          method: 'PUT',
          headers: {
            'Content-Type': data.contentType,
            'x-ms-blob-type': 'BlockBlob'
          }
        });

        // Save finalization data
        const finalizationData = {
          type: data.options.type,
          blob_name,
          container,
          job_id: data.options.jobId,
          metadata: data.options.metadata || {},
          capture_time: data.options.captureTime
        };
        localStorage.setItem(`pending_finalize_${result.id}`, JSON.stringify(finalizationData));

        // Remove from pending queue
        localStorage.removeItem(key);
        console.log(`[BackgroundUpload] Retry successful, upload started: ${data.filename}`);

      } catch (error: any) {
        console.error(`[BackgroundUpload] Retry failed for ${key}:`, error.message);

        // Increment retry count
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        data.retryCount = (data.retryCount || 0) + 1;

        if (data.retryCount >= 20) {
          // After 20 retries, give up and clean up
          console.warn(`[BackgroundUpload] Max retries reached, removing: ${data.filename}`);
          localStorage.removeItem(key);

          // Clean up filesystem
          try {
            await Filesystem.deleteFile({
              path: data.tempFilename,
              directory: Directory.Cache
            });
          } catch (e) {
            // Ignore cleanup errors
          }
        } else {
          localStorage.setItem(key, JSON.stringify(data));
        }
      }
    }
  }

  /**
   * Wait for upload to complete and then finalize
   */
  private async waitForUploadAndFinalize(
    uploadId: string,
    finalizationData: any,
    tempFilename: string
  ): Promise<{ success: boolean; id?: number; error?: string }> {
    this.debug(`waitForUploadAndFinalize: uploadId=${uploadId}`);

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        // If upload takes more than 30 seconds, assume it's continuing in background
        this.debug(`Upload ${uploadId} timeout - continuing in background`);
        resolve({ success: true, id: -1 }); // -1 indicates background upload in progress
      }, 30000);

      this.uploadCallbacks.set(uploadId, async (event) => {
        this.debug(`Upload event: status=${event.status}, percent=${event.percent}`);

        if (event.status === 'completed') {
          clearTimeout(timeout);
          this.debug('Upload completed, finalizing...');

          try {
            // Finalize the upload (create database record)
            const finalizeResponse = await api.post(`${API_CONFIG.BASE_URL}/api/uploads/finalize`, finalizationData);
            this.debug(`Finalize response: success=${finalizeResponse.data.success}`);

            // Clean up temp file
            await Filesystem.deleteFile({
              path: tempFilename,
              directory: Directory.Cache
            }).catch(() => {}); // Ignore errors

            // Remove pending finalization data
            localStorage.removeItem(`pending_finalize_${uploadId}`);

            if (finalizeResponse.data.success) {
              this.debug(`SUCCESS - finalized ID: ${finalizeResponse.data.data.id}`);
              resolve({ success: true, id: finalizeResponse.data.data.id });
            } else {
              this.debug(`Finalize failed: ${finalizeResponse.data.error}`);
              resolve({ success: false, error: finalizeResponse.data.error });
            }
          } catch (error: any) {
            this.debug(`Finalize error: ${error.message}`);
            resolve({ success: false, error: error.message });
          }

          this.uploadCallbacks.delete(uploadId);
        } else if (event.status === 'failed') {
          clearTimeout(timeout);
          this.debug(`Upload FAILED: ${event.error}`);
          resolve({ success: false, error: event.error });
          this.uploadCallbacks.delete(uploadId);
        }
      });
    });
  }

  /**
   * Check for and finalize any pending uploads (call on app resume)
   */
  async finalizePendingUploads() {
    if (!isNativeApp()) return;

    const keys = Object.keys(localStorage).filter(k => k.startsWith('pending_finalize_'));
    console.log(`[BackgroundUpload] Checking ${keys.length} pending uploads...`);

    for (const key of keys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.blob_name) {
          console.log(`[BackgroundUpload] Finalizing pending upload: ${data.blob_name}`);

          const response = await api.post(`${API_CONFIG.BASE_URL}/api/uploads/finalize`, data);

          if (response.data.success) {
            localStorage.removeItem(key);
            console.log(`[BackgroundUpload] Pending upload finalized: ${response.data.data.id}`);
          } else if (response.data.code === 'BLOB_NOT_FOUND') {
            // Blob doesn't exist yet - upload may still be in progress
            // Keep the pending entry for next retry
            console.log(`[BackgroundUpload] Blob not found yet, will retry later: ${data.blob_name}`);

            // Track retry count to eventually give up
            data.retryCount = (data.retryCount || 0) + 1;
            if (data.retryCount >= 10) {
              // After 10 retries, assume upload failed and remove
              console.warn(`[BackgroundUpload] Max retries reached, removing: ${data.blob_name}`);
              localStorage.removeItem(key);
            } else {
              localStorage.setItem(key, JSON.stringify(data));
            }
          }
        }
      } catch (error: any) {
        console.error(`[BackgroundUpload] Failed to finalize pending upload:`, error);

        // If it's a 400 error with BLOB_NOT_FOUND, don't remove yet
        if (error.response?.data?.code === 'BLOB_NOT_FOUND') {
          console.log(`[BackgroundUpload] Blob not found, will retry later`);
        }
      }
    }
  }

  /**
   * PWA fallback - standard fetch upload
   */
  private async uploadFilePWA(
    file: File | Blob,
    filename: string,
    options: BackgroundUploadOptions
  ): Promise<{ success: boolean; id?: number; error?: string }> {
    try {
      // For PWA, use the existing queue-based upload
      // This is handled by upload-queue.service.ts
      console.log('[BackgroundUpload] PWA mode - using standard upload queue');
      return { success: true, id: -2 }; // -2 indicates PWA queue mode
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Convert File/Blob to base64
   */
  private fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Remove data URL prefix
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Remove an upload from the queue
   */
  async cancelUpload(uploadId: string) {
    if (!isNativeApp()) return;

    try {
      await Uploader.removeUpload({ id: uploadId });
      this.uploadCallbacks.delete(uploadId);
      localStorage.removeItem(`pending_finalize_${uploadId}`);
    } catch (error) {
      console.error('[BackgroundUpload] Failed to cancel upload:', error);
    }
  }

  /**
   * Get native upload queue stats
   * Returns counts of pending and in-progress native uploads
   */
  getNativeQueueStats(): { pendingRetry: number; inProgress: number } {
    if (!isNativeApp()) {
      return { pendingRetry: 0, inProgress: 0 };
    }

    // Count uploads waiting to retry (failed due to network)
    const pendingRetryKeys = Object.keys(localStorage).filter(k => k.startsWith('pending_native_upload_'));

    // Count uploads in progress (uploaded to blob, waiting for finalization)
    const inProgressKeys = Object.keys(localStorage).filter(k => k.startsWith('pending_finalize_'));

    return {
      pendingRetry: pendingRetryKeys.length,
      inProgress: inProgressKeys.length
    };
  }
}

export const backgroundUploadService = new BackgroundUploadService();
